import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Mail, Phone, MessageSquare, X, RefreshCw, Copy, CheckCheck, Search, Check } from 'lucide-react';
import { koffieClient } from '../lib/supabase';

// ─── Tokens ───────────────────────────────────────────────────────────────────

const t = {
  bg:     '#f2f0eb',
  white:  '#ffffff',
  text:   '#0c0c0b',
  muted:  '#8a8780',
  body:   '#5b5953',
  lines:  '#dcd9d1',
  accent: '#c75a0a',
  sans:   "'Jost', system-ui, sans-serif",
  serif:  "'Playfair Display', Georgia, serif",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  height: 100%;
  width: 100%;
  background: ${t.bg};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Page = styled.div`
  padding: 36px 40px 80px;
  max-width: 780px;
  margin: 0 auto;
  animation: ${fadeUp} 0.3s ease both;

  @media (max-width: 600px) {
    padding: 24px 20px 64px;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 900;
  color: ${t.text};
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const FilterBtn = styled.button`
  background: ${p => p.$active ? t.text : t.white};
  border: 1px solid ${p => p.$active ? t.text : t.lines};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 500;
  font-family: ${t.sans};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${p => p.$active ? t.bg : t.muted};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { color: ${p => p.$active ? t.bg : t.text}; border-color: ${t.text}; }
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 500;
  font-family: ${t.sans};
  color: ${t.muted};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.15s;
  &:hover { color: ${t.text}; }
`;

// ─── Search ───────────────────────────────────────────────────────────────────

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 7px 12px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.text};
  &::placeholder { color: ${t.muted}; opacity: 0.5; }
  &:focus { outline: none; }
`;

// ─── Grid ─────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  padding-bottom: 40px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: ${p => p.$done ? 'rgba(12,12,11,0.025)' : t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: background 0.12s;
  opacity: ${p => p.$done ? 0.6 : 1};
  &:hover { background: ${t.bg}; opacity: 1; }
`;

const CardHeader = styled.div`
  padding: 14px 16px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CardNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const CardName = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: ${t.text};
`;

const DoneCheck = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: rgba(12,12,11,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${t.muted};
`;

const CardDate = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  white-space: nowrap;
  flex-shrink: 0;
`;

const CardMessage = styled.div`
  margin: 0 16px 12px;
  font-family: ${t.sans};
  font-size: 12px;
  color: ${t.muted};
  line-height: 1.6;
  background: ${t.bg};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 10px 12px;
  white-space: pre-wrap;
  max-height: 88px;
  overflow: hidden;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 32px;
    background: linear-gradient(transparent, ${t.bg});
    border-radius: 0 0 6px 6px;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 12px;
  border-top: 1px solid ${t.lines};
`;

const MethodLabel = styled.div`
  flex: 1;
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CompleteBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1.5px solid ${p => p.$done ? t.text : t.lines};
  background: ${p => p.$done ? t.text : 'transparent'};
  color: ${p => p.$done ? t.bg : t.lines};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    border-color: ${t.text};
    background: ${p => p.$done ? '#1a1a18' : 'rgba(12,12,11,0.06)'};
    color: ${p => p.$done ? t.bg : t.text};
  }
`;

const DoneLink = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  background: none;
  border: none;
  border-top: 1px solid ${t.lines};
  padding: 14px 0 0;
  margin-top: 4px;
  width: 100%;
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  color: ${t.muted};
  cursor: pointer;
  text-align: left;
  transition: color 0.15s;
  &:hover { color: ${t.text}; }
`;

// ─── Empty / Loading ──────────────────────────────────────────────────────────

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: ${t.text};
  opacity: 0.5;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: ${t.sans};
  font-size: 12px;
  color: ${t.muted};
  opacity: 0.5;
`;

// ─── Overlay ──────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12,12,11,0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
  @media (min-width: 600px) { align-items: center; padding: 24px; }
`;

// ─── Detail modal ─────────────────────────────────────────────────────────────

const Modal = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px 8px 0 0;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px 22px 32px;
  @media (min-width: 600px) { border-radius: 8px; max-width: 520px; }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
`;

const ModalName = styled.h2`
  font-family: ${t.serif};
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: 900;
  color: ${t.text};
  letter-spacing: -0.02em;
  margin: 0 0 3px;
`;

const ModalDate = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
`;

const CloseBtn = styled.button`
  width: 30px; height: 30px;
  border-radius: 6px;
  border: 1px solid ${t.lines};
  background: ${t.white};
  color: ${t.muted};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0;
  &:hover { background: ${t.bg}; }
`;

const ModalSection = styled.div`margin-bottom: 16px;`;

const ModalLabel = styled.div`
  font-family: ${t.sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${t.muted};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalValue = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalMessage = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.body};
  line-height: 1.65;
  background: ${t.bg};
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 12px 14px;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${t.lines};
`;

const FooterBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: ${t.sans};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  border: 1.5px solid ${p => p.$primary ? t.text : t.lines};
  background: ${p => p.$primary ? t.text : 'transparent'};
  color: ${p => p.$primary ? t.bg : t.body};
  &:hover {
    background: ${p => p.$primary ? '#1a1a18' : 'rgba(12,12,11,0.04)'};
    color: ${p => p.$primary ? t.bg : t.text};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METHOD_ICONS  = { email: Mail, phone: Phone, whatsapp: MessageSquare };
const METHOD_LABELS = { email: 'E-mail', phone: 'Telefoon', whatsapp: 'WhatsApp' };

function fmtDate(iso) {
  return new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtDateLong(iso) {
  return new Date(iso).toLocaleString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function KoffieContact() {
  const [all, setAll]               = useState([]);
  const [query, setQuery]           = useState('');
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [copied, setCopied]         = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [showDone, setShowDone]     = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const { data } = await koffieClient
      .from('contact-submissions')
      .select('*')
      .order('submitted_at', { ascending: false });
    setAll(data ?? []);
    setLastFetched(new Date());
    setLoading(false);
  };

  const toggleCompleted = async (item, e) => {
    e?.stopPropagation();
    const next = !item.is_completed;
    setAll(prev => prev.map(r => r.id === item.id ? { ...r, completed: next } : r));
    if (selected?.id === item.id) setSelected(s => ({ ...s, completed: next }));
    await koffieClient
      .from('contact-submissions')
      .update({ is_completed: next })
      .eq('id', item.id);
  };

  const base = query.trim()
    ? all.filter(r =>
        r.name?.toLowerCase().includes(query.toLowerCase()) ||
        r.message?.toLowerCase().includes(query.toLowerCase()) ||
        r.email?.toLowerCase().includes(query.toLowerCase())
      )
    : all;

  const doneCount = all.filter(r => r.is_completed).length;

  return (
    <Wrap>
      <Page>
        <PageHeaderRow>
          <PageTitle>Contactinzendingen</PageTitle>
          <HeaderRight>
            <RefreshBtn onClick={loadAll}>
              <RefreshCw size={11} />
              {lastFetched
                ? lastFetched.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
                : 'Verversen'}
            </RefreshBtn>
          </HeaderRight>
        </PageHeaderRow>

        <SearchBar>
          <Search size={13} color={t.muted} />
          <SearchInput
            placeholder="Zoeken op naam, e-mail of bericht…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </SearchBar>

        {loading ? (
          <EmptyState><EmptyText>Laden…</EmptyText></EmptyState>
        ) : (showDone ? base : base.filter(r => !r.is_completed)).length === 0 ? (
          <EmptyState>
            <EmptyTitle>Geen inzendingen</EmptyTitle>
            <EmptyText>{query ? 'Geen resultaten voor deze zoekopdracht.' : 'Er zijn nog geen openstaande inzendingen.'}</EmptyText>
          </EmptyState>
        ) : (
          <Grid>
            {(showDone ? base : base.filter(r => !r.is_completed)).map(c => {
              const MethodIcon = METHOD_ICONS[c.contact_method] ?? Mail;
              return (
                <Card key={c.id} $done={c.is_completed} onClick={() => setSelected(c)}>
                  <CardHeader>
                    <CardNameRow>
                      {c.is_completed && <DoneCheck><Check size={10} strokeWidth={2.5} /></DoneCheck>}
                      <CardName>{c.name}</CardName>
                    </CardNameRow>
                    <CardDate>{fmtDate(c.submitted_at)}</CardDate>
                  </CardHeader>

                  {c.message && (
                    <CardMessage>{c.message}</CardMessage>
                  )}

                  <CardFooter>
                    <MethodLabel>
                      <MethodIcon size={12} />
                      {METHOD_LABELS[c.contact_method] ?? c.contact_method ?? 'Onbekend'}
                    </MethodLabel>
                    <CompleteBtn
                      $done={c.is_completed}
                      onClick={e => toggleCompleted(c, e)}
                      title={c.is_completed ? 'Heropenen' : 'Markeer als voltooid'}
                    >
                      <Check size={11} strokeWidth={2.5} />
                    </CompleteBtn>
                  </CardFooter>
                </Card>
              );
            })}
          </Grid>
        )}

        {!showDone && doneCount > 0 && (
          <DoneLink onClick={() => setShowDone(true)}>
            <Check size={12} strokeWidth={2.5} />
            Voltooide contactaanvragen ({doneCount})
          </DoneLink>
        )}
        {showDone && (
          <DoneLink onClick={() => setShowDone(false)}>
            ← Terug naar openstaand
          </DoneLink>
        )}
      </Page>

      {/* Detail modal */}
      {selected && (
        <Overlay onClick={() => setSelected(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalName>{selected.name}</ModalName>
                <ModalDate>{fmtDateLong(selected.submitted_at)}</ModalDate>
              </div>
              <CloseBtn onClick={() => setSelected(null)}><X size={14} /></CloseBtn>
            </ModalHeader>

            <ModalSection>
              <ModalLabel>Contactgegevens</ModalLabel>
              {selected.email && <ModalValue><Mail size={13} /> {selected.email}</ModalValue>}
              {selected.phone && <ModalValue style={{ marginTop: 6 }}><Phone size={13} /> {selected.phone}</ModalValue>}
            </ModalSection>

            {selected.contact_method && (
              <ModalSection>
                <ModalLabel>Voorkeur contact via</ModalLabel>
                <ModalValue>
                  {(() => { const I = METHOD_ICONS[selected.contact_method] ?? Mail; return <I size={13} />; })()}
                  {METHOD_LABELS[selected.contact_method] ?? selected.contact_method}
                </ModalValue>
              </ModalSection>
            )}

            {selected.message && (
              <ModalSection>
                <ModalLabel>
                  <span>Bericht</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selected.message);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: '10px', fontWeight: 500, fontFamily: t.sans,
                      color: copied ? '#22c55e' : t.muted,
                      textTransform: 'uppercase', letterSpacing: '0.10em', padding: 0,
                    }}
                  >
                    {copied ? <CheckCheck size={10} /> : <Copy size={10} />}
                    {copied ? 'Gekopieerd' : 'Kopiëren'}
                  </button>
                </ModalLabel>
                <ModalMessage>{selected.message}</ModalMessage>
              </ModalSection>
            )}

            <ModalFooter>
              <FooterBtn onClick={() => setSelected(null)}>Sluiten</FooterBtn>
              <FooterBtn
                $primary
                disabled={completing}
                onClick={async () => {
                  setCompleting(true);
                  await toggleCompleted(selected);
                  setCompleting(false);
                  setSelected(null);
                }}
              >
                <Check size={13} strokeWidth={2.5} />
                {selected.is_completed ? 'Heropenen' : 'Markeer als voltooid'}
              </FooterBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
